import { AMT_UI_SCALE, D, type Money } from '@wave/shared';

/**
 * Recalc v2 — пересчёт формы заявки по происхождению значений (provenance).
 *
 * Проблема v1: автоподставленный рыночный курс был неотличим от введённого
 * руками — форма пересчитывала суммы по нему и затирала ввод оператора.
 *
 * Модель v2: каждое поле несёт src ('user' | 'auto') и seq (монотонный номер
 * последнего ручного ввода). Инвариант: **введённое руками никогда не
 * перезаписывается автоматически** — пересчитывается «плавающее» поле:
 *   1) пустое; иначе
 *   2) src='auto'; иначе
 *   3) (все ручные) — с наименьшим seq, оно при этом помечается auto.
 * При прочих равных пересчитываем сумму, а не курс (курс — «настройка»).
 *
 * Курс хранится в каноническом направлении («сколько get за 1 give»)
 * с 8 знаками — направление отображения и округление до 4 знаков
 * для API — забота UI-слоя.
 */

export type FieldSrc = 'user' | 'auto';

export interface Tracked {
  value: Money | '';
  src: FieldSrc;
  /** Номер последнего ручного ввода; 0 = никогда не вводилось руками. */
  seq: number;
}

export interface ClientState {
  give: Tracked;
  get: Tracked;
  rate: Tracked;
}
export type ClientField = keyof ClientState;

export interface ExecState {
  /** exGetAmt — сколько платим исполнителю. */
  amt: Tracked;
  /** exRate — сколько clGetCcy за 1 exGetCcy. */
  rate: Tracked;
}
export type ExecField = keyof ExecState;

const RATE_CALC_SCALE = 8; // внутренняя точность курса; UI округляет при отображении

function pos(v: string): boolean {
  return v !== '' && D(v).gt(0);
}

/** Выбрать плавающее поле из кандидатов по правилам провенанса. */
function pickFloating<K extends string>(
  candidates: K[],
  fields: Record<K, Tracked>,
  computable: (k: K) => boolean,
): K | null {
  const ranked = [...candidates].sort((a, b) => {
    const fa = fields[a];
    const fb = fields[b];
    // 1. пустые — первыми
    const emptyA = fa.value === '' ? 0 : 1;
    const emptyB = fb.value === '' ? 0 : 1;
    if (emptyA !== emptyB) return emptyA - emptyB;
    // 2. auto раньше user
    const autoA = fa.src === 'auto' ? 0 : 1;
    const autoB = fb.src === 'auto' ? 0 : 1;
    if (autoA !== autoB) return autoA - autoB;
    // 3. меньший seq (старее) раньше
    if (fa.seq !== fb.seq) return fa.seq - fb.seq;
    // 4. стабильность: исходный порядок candidates (сумма раньше курса)
    return candidates.indexOf(a) - candidates.indexOf(b);
  });
  for (const k of ranked) {
    if (computable(k)) return k;
  }
  return null;
}

/**
 * Пересчёт клиентской тройки give × rate = get после правки поля `edited`.
 * Контракт: `edited` не модифицируется; плавающее поле получает новое
 * значение и src='auto'. Если данных не хватает — состояние возвращается как есть.
 */
export function resolveClient(state: ClientState, edited: ClientField): ClientState {
  const { give, get, rate } = state;
  // Порядок кандидатов: суммы раньше курса (курс — «настройка»).
  // Очищенное edited-поле само становится кандидатом — «очистить = отпустить».
  const candidates = (['get', 'give', 'rate'] as ClientField[]).filter(
    (f) => f !== edited || state[f].value === '',
  );

  const computable = (k: ClientField): boolean => {
    if (k === 'get') return pos(give.value) && pos(rate.value);
    if (k === 'give') return pos(get.value) && pos(rate.value);
    return pos(give.value) && pos(get.value); // rate
  };

  const floating = pickFloating(candidates, state, computable);
  if (!floating) return state;

  const next = { ...state };
  if (floating === 'get') {
    next.get = { value: D(give.value).times(D(rate.value)).toFixed(AMT_UI_SCALE), src: 'auto', seq: get.seq };
  } else if (floating === 'give') {
    next.give = { value: D(get.value).dividedBy(D(rate.value)).toFixed(AMT_UI_SCALE), src: 'auto', seq: give.seq };
  } else {
    next.rate = {
      value: D(get.value).dividedBy(D(give.value)).toDecimalPlaces(RATE_CALC_SCALE).toString(),
      src: 'auto',
      seq: rate.seq,
    };
  }
  return next;
}

/**
 * Пересчёт пары исполнителя amt × rate = clGet.
 * edited=null — clGet изменился извне (каскад с клиентской стороны).
 */
export function resolveExec(state: ExecState, clGet: Money | '', edited: ExecField | null): ExecState {
  if (!pos(clGet)) return state;
  const { amt, rate } = state;
  const candidates = (['amt', 'rate'] as ExecField[]).filter((f) => f !== edited);

  const computable = (k: ExecField): boolean => {
    if (k === 'amt') return pos(rate.value);
    return pos(amt.value); // rate
  };

  const floating = pickFloating(candidates, state, computable);
  if (!floating) return state;

  const next = { ...state };
  if (floating === 'amt') {
    next.amt = { value: D(clGet).dividedBy(D(rate.value)).toFixed(AMT_UI_SCALE), src: 'auto', seq: amt.seq };
  } else {
    next.rate = {
      value: D(clGet).dividedBy(D(amt.value)).toDecimalPlaces(RATE_CALC_SCALE).toString(),
      src: 'auto',
      seq: rate.seq,
    };
  }
  return next;
}
